// src/redux/slices/authSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { setUserData, clearUserData, getUserData } from "../../utils/authUtils";
import { API_BASE_URL } from "../../config";

interface AuthState {
  user: any;
  loading: boolean; // generic loader (login/password, logout)
  error: string | null;

  // OTP Flow
  otpSending: boolean;
  otpSent: boolean;
  otpError: string | null;
  verifying: boolean;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,

  otpSending: false,
  otpSent: false,
  otpError: null,
  verifying: false,
};

// --------------------- Normal login (password) ---------------------
export const loginUser = createAsyncThunk(
  "auth/login",
  async (
    {
      mobile,
      password,
      fcm_token,
    }: { mobile: string; password: string; fcm_token: string },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("mobile", mobile);
      formData.append("password", password ?? "");
      formData.append("fcm_token", fcm_token ?? "");

      const response = await axios.post(`${API_BASE_URL}/login`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.status === "success") {
        const userRole = (
          response.data?.Customerdetail?.role ||
          response.data?.customerdetail?.role ||
          response.data?.user?.role ||
          response.data?.role ||
          ""
        )
          .toString()
          .toLowerCase();

        if (
          userRole === "messenger" ||
          userRole === "messanger" ||
          userRole.includes("mess")
        ) {
          return rejectWithValue(
            "Messenger accounts are not allowed to access this application. Please contact support.",
          );
        }

        return response.data;
      } else {
        return rejectWithValue(response.data?.message || "Login failed");
      }
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Login failed",
      );
    }
  },
);

// --------------------- OTP: send ---------------------
export const sendLoginOtp = createAsyncThunk(
  "auth/sendLoginOtp",
  async (
    {
      mobile,
      fcm_token,
    }: {
      mobile: string;
      fcm_token?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("mobile", mobile);
      // per cURL, password can be empty when we want OTP
      formData.append("password", "");
      if (fcm_token) formData.append("fcm_token", fcm_token);

      const response = await axios.post(`${API_BASE_URL}/login`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // API returns success when OTP is sent to user
      if (response.data?.status === "success") {
        return { mobile };
      }
      return rejectWithValue(response.data?.message || "Failed to send OTP");
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to send OTP",
      );
    }
  },
);

// --------------------- OTP: verify ---------------------
export const verifyLoginOtp = createAsyncThunk(
  "auth/verifyLoginOtp",
  async (
    { mobile, otp }: { mobile: string; otp: string },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("mobile", mobile);
      formData.append("otp", otp);

      const response = await axios.post(
        `${API_BASE_URL}/verify_login_otp`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (response.data?.status === "success") {
        // enforce messenger role block here as well
        const userRole = (
          response.data?.Customerdetail?.role ||
          response.data?.customerdetail?.role ||
          response.data?.user?.role ||
          response.data?.role ||
          ""
        )
          .toString()
          .toLowerCase();

        if (
          userRole === "messenger" ||
          userRole === "messanger" ||
          userRole.includes("mess")
        ) {
          return rejectWithValue(
            "Messenger accounts are not allowed to access this application. Please contact support.",
          );
        }

        return response.data;
      }
      return rejectWithValue(response.data?.message || "Invalid OTP");
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Invalid OTP",
      );
    }
  },
);

// --------------------- Logout ---------------------
export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const userData = JSON.parse(sessionStorage.getItem("user") || "{}");
      const m_id = userData?.Customerdetail?.id || userData?.id || "";
      const role = userData?.Customerdetail?.role || userData?.role || "";
      const token = userData?.Customerdetail?.token || userData?.token || "";

      if (!m_id || !role)
        return rejectWithValue("Missing user ID or role for logout");
      if (!token) return rejectWithValue("Missing authentication token");

      const formData = new FormData();
      formData.append("m_id", m_id);
      formData.append("role", role);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL || "https://my.axlpl.com/messenger/services_v7"}/logout`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.status === "success") return response.data;
      return rejectWithValue(response.data?.message || "Logout failed");
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Logout API failed",
      );
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    restoreUser: (state) => {
      const userData = getUserData();
      if (userData) {
        state.user = userData;
        state.loading = false;
        state.error = null;
      }
    },
    logoutLocal: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      clearUserData();
    },
    resetOtp: (state) => {
      state.otpSending = false;
      state.otpSent = false;
      state.otpError = null;
      state.verifying = false;
    },
  },
  extraReducers: (builder) => {
    // password login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;

        const userRole = (
          action.payload?.Customerdetail?.role ||
          action.payload?.customerdetail?.role ||
          action.payload?.user?.role ||
          action.payload?.role ||
          ""
        )
          .toString()
          .toLowerCase();

        if (
          userRole === "messenger" ||
          userRole === "messanger" ||
          userRole.includes("mess")
        ) {
          state.error =
            "Messenger accounts are not allowed to access this application. Please contact support.";
          state.user = null;
          return;
        }

        if (setUserData(action.payload)) {
          state.user = action.payload;
        } else {
          state.error =
            "Messenger accounts are not allowed to access this application. Please contact support.";
          state.user = null;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Login failed";
      });

    // send OTP
    builder
      .addCase(sendLoginOtp.pending, (state) => {
        state.otpSending = true;
        state.otpError = null;
        state.otpSent = false;
      })
      .addCase(sendLoginOtp.fulfilled, (state) => {
        state.otpSending = false;
        state.otpSent = true;
      })
      .addCase(sendLoginOtp.rejected, (state, action) => {
        state.otpSending = false;
        state.otpError = (action.payload as string) || "Failed to send OTP";
        state.otpSent = false;
      });

    // verify OTP
    builder
      .addCase(verifyLoginOtp.pending, (state) => {
        state.verifying = true;
        state.error = null;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.verifying = false;

        const userRole = (
          action.payload?.Customerdetail?.role ||
          action.payload?.customerdetail?.role ||
          action.payload?.user?.role ||
          action.payload?.role ||
          ""
        )
          .toString()
          .toLowerCase();

        if (
          userRole === "messenger" ||
          userRole === "messanger" ||
          userRole.includes("mess")
        ) {
          state.error =
            "Messenger accounts are not allowed to access this application. Please contact support.";
          state.user = null;
          return;
        }

        if (setUserData(action.payload)) {
          state.user = action.payload;
        } else {
          state.error =
            "Failed to store user session. Please try logging in again.";
          state.user = null;
        }
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.verifying = false;
        state.error = (action.payload as string) || "Invalid OTP";
      });

    // logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.error = null;
        clearUserData();
      })
      .addCase(logoutUser.rejected, (state) => {
        state.loading = false;
        state.user = null;
        clearUserData();
      });
  },
});

export const { restoreUser, logoutLocal, resetOtp } = authSlice.actions;
export default authSlice.reducer;
